import PrivacyNavigation, { privacyRoutes } from "./PrivacyNavigation";
import { useLocation } from "react-router-dom";
import { openCookiePreferences } from "../lib/consent";
import { Link } from "./CanonicalLink";
import TopBar from "./TopBar";
import Masthead from "./Masthead";
import Footer from "./Footer";
import Reveal from "./Reveal";
import { publication } from "../lib/publication";

const H2 = ({ children }) => (
  <h2 className="mt-10 first:mt-0 font-display text-2xl font-semibold text-[var(--ink)]">
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="mt-4 text-[15px] leading-8 text-[var(--muted)]">{children}</p>
);

const UL = ({ items }) => (
  <ul className="mt-4 space-y-3">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3 text-[15px] leading-7 text-[var(--muted)]">
        <span className="mt-2.5 w-1.5 h-1.5 rotate-45 bg-[#cf0a0a] shrink-0" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const chipCls =
  "inline-flex items-center gap-1.5 rounded-full bg-[var(--bg-soft)] px-4 py-2.5 text-[13px] font-bold normal-case tracking-normal text-[#777777] dark:text-[#b5afa6] hover:bg-[#cf0a0a] hover:text-white dark:hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#cf0a0a] transition-colors duration-200";

const LinkRow = ({ links }) => (
  <div className="mt-5 flex flex-wrap items-center gap-3">
    {links.map((l) =>
      l.href.startsWith("/") ? (
        <Link key={l.label} to={l.href} data-testid={l.testid} className={chipCls}>
          {l.label}
        </Link>
      ) : (
        <a
          key={l.label}
          href={l.href}
          target={l.href.startsWith("mailto:") ? undefined : "_blank"}
          rel={l.href.startsWith("mailto:") ? undefined : "noreferrer"}
          data-testid={l.testid}
          className={chipCls}
        >
          {l.label}
        </a>
      )
    )}
  </div>
);

export const LegalShell = ({ testid, title, intro, children }) => {
  const { pathname } = useLocation();
  const privacy = privacyRoutes.includes(pathname.replace(/\/$/, ""));
  return (
  <div data-testid={testid}>
    <TopBar />
    <Masthead />
    <main>
      <section className="bg-[var(--bg)]">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 pt-10 sm:pt-14">
          <Reveal>
            <div className="border-t-[3px] border-[#cf0a0a] bg-[var(--bg-soft)] px-5 sm:px-7 py-6">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.24em] text-[var(--accent)]">
                The Standard
              </p>
              <h1
                data-testid={`${testid}-title`}
                className="mt-1 font-display text-3xl sm:text-4xl font-semibold"
              >
                {title}
              </h1>
              {intro && (
                <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-[var(--muted)]">
                  {intro}
                </p>
              )}
            </div>
          </Reveal>
        </div>
      </section>
      <section className={`mx-auto px-4 sm:px-6 pt-10 pb-20 ${privacy ? "max-w-[1260px] grid gap-8 lg:grid-cols-[270px_minmax(0,1fr)] lg:gap-12" : "max-w-[820px]"}`}>
        {privacy && <PrivacyNavigation />}
        <div className="min-w-0" data-testid={`${testid}-content`}>{children}</div>
      </section>
    </main>
    <Footer />
  </div>
);
};

/* ---------------- Privacy Policy (content from standard.gm/privacy-policy) ---------------- */

export const PrivacyPolicyPage = () => (
  <LegalShell
    testid="privacy-policy"
    title="Privacy Policy"
    intro="How The Standard handles information, advertising, embedded media and your privacy requests."
  >
    <H2>Who we are and what this policy covers</H2>
    <P>
      The Standard Newspaper is published by XL Standard Co. Ltd. in The Gambia. The publisher is
      responsible for personal information it processes in operating this website and communicating
      with readers.
    </P>
    <P>
      This notice covers the new Standard website at standard.gm and its reader services. Other
      websites, social platforms, payment services and applications have their own notices. A
      service-specific notice will explain any additional processing when a new service is
      introduced.
    </P>

    <H2>Information we receive</H2>
    <P>
      You can read stories without creating an account. When your browser requests a page, image,
      video or advertisement, the relevant service receives technical information needed to deliver
      it. Searching the website sends your search terms to our service; please avoid entering
      passwords or other sensitive information into search.
    </P>
    <UL
      items={[
        "Information you send us: your name, email address, correspondence and any details or attachments you choose to include in enquiries, complaints, corrections or news tips.",
        "Technical information: an IP address, browser or device information, requested URLs, search terms, referring pages, request times and error or security records may appear in hosting and delivery logs.",
        "Advertising interactions: our direct-ad system counts campaign impressions and clicks. These totals do not by themselves identify a named reader. Ad requests and click redirects may also generate ordinary server logs.",
        "Additional services: if you use a separately offered subscription, newsletter, payment or account service, the information required and its purpose will be explained when that service is offered.",
      ]}
    />

    <H2>How and why information is used</H2>
    <P>
      We use information to deliver pages and search results, respond to enquiries, investigate
      corrections, support reporting, maintain security and reliability, and measure direct
      advertising performance.
    </P>
    <P>
      Where applicable, processing is based on the legal ground appropriate to the activity:
      consent, providing a service you request, meeting a legal obligation, or legitimate purposes
      such as operating and protecting the website. We will obtain consent where required. Consent
      to optional marketing can be withdrawn without affecting the lawfulness of earlier processing.
    </P>
    <UL
      items={[
        "Correspondence about a news tip may be used to assess and investigate the information you provide.",
        "A correction or complaint may require us to retain the article reference and relevant correspondence.",
        "Advertising reports may include campaign totals, rather than identifying information about individual readers.",
      ]}
    />

    <H2>Cookies, advertising and embedded media</H2>
    <P>Google AdSense is currently disabled on this website. Third-party ad-code frames load only after you allow third-party advertising in Cookie Preferences. Providers may use cookies for delivery and measurement. You can change your choice at any time.</P>
    <P>With your permission, Google Analytics measures visits and how readers use this website. The Google tag stays off until you allow visitor analytics in Cookie Preferences. You can withdraw that permission at any time through Your Privacy Choices. Google advertising personalization is not enabled by this integration.</P>
    <P>
      Cookies and similar browser storage can be used by delivery, security, media and advertising
      services. The technologies present depend on the services enabled and the content you open. A
      cookie inventory and any required consent controls should accompany the introduction of
      additional optional tracking.
    </P>
    <P>
      The new website's direct-ad counter records campaign totals rather than setting its own
      reader-tracking cookie. This does not prevent hosting services, advertisers' destination
      websites or third-party media from processing information separately.
    </P>
    <P>
      Our video players use YouTube's privacy-enhanced embed domain where configured. Playing a
      video still communicates with Google/YouTube and is subject to its terms and privacy notice.
      Thumbnails and article media can also load from external servers before playback.
    </P>
    <P>
      Where Google advertising or analytics services are enabled, they may receive technical
      information and use cookies or similar technologies. Google provides its own advertising and
      activity controls. Social sharing links send you to the selected provider when you choose to
      use them.
    </P>
    <LinkRow
      links={[
        { label: "Your Privacy Choices", href: "/your-privacy-choices", testid: "privacy-policy-link-choices" },
        { label: "How Google uses information from partner sites", href: "https://policies.google.com/technologies/partner-sites" },
        { label: "Google Privacy Policy", href: "https://policies.google.com/privacy" },
      ]}
    />

    <H2>Who may receive information</H2>
    <P>
      Information needed to deliver this website may be processed by hosting, content delivery,
      security and technical service providers, including Cloudflare and the servers supplying our
      WordPress content. Video, social and advertising providers process information when their
      services are used. Their own policies explain their independent processing.
    </P>
    <P>
      We may disclose relevant information where required by law or a valid legal process, to
      investigate abuse or security incidents, or to protect lawful rights. The scope of any
      disclosure should be limited to what is appropriate for its purpose.
    </P>
    <P>
      A reader's voluntary submission is not automatically permission to publish private contact
      details. However, information supplied for reporting may be assessed, verified and used in
      journalism, subject to applicable law and editorial judgement.
    </P>

    <H2>Processing outside The Gambia</H2>
    <P>
      The internet services used to deliver content, protect the website and provide embedded media
      may process information in other countries. Privacy laws in those countries may differ from
      Gambian law. Where cross-border safeguards are required, the applicable legal requirements
      govern the transfer. Provider notices explain their processing locations and safeguards.
    </P>

    <H2>Retention and security</H2>
    <P>
      Information should be kept for as long as needed for the purpose for which it was collected,
      including responding to correspondence, security, legal obligations and resolving disputes.
      Published journalism and relevant editorial records may need to be retained for the public
      record. Retention periods vary by record and by service provider; contact us about a
      particular record.
    </P>
    <P>
      We use HTTPS for this website. No website, email service or method of storage can guarantee
      complete security. Do not send passwords, payment credentials or unnecessary identity
      documents in ordinary email. Contact the newsroom before sending sensitive confidential
      material to discuss an appropriate channel.
    </P>

    <H2>Your requests and rights</H2>
    <P>
      Depending on applicable law and the circumstances, you may request access to personal
      information, correction of inaccurate information, deletion, restriction of processing,
      objection to certain processing, or withdrawal of consent. Other rights, including
      portability where applicable, may also be available.
    </P>
    <P>
      We may need proportionate information to verify a request and identify the relevant records.
      Do not send an identity document unless we specifically explain why it is needed. We will
      explain any applicable limitation, including legal retention obligations, third-party rights
      or lawful journalistic and public-interest considerations. A privacy request does not
      automatically require removal of an accurate, lawfully published news report.
    </P>
    <P>
      If you are dissatisfied with how a request is handled, you may pursue a complaint with the
      competent authority under applicable Gambian law, including the Information Commission where
      it has jurisdiction. Mandatory rights under other applicable laws are not excluded by this
      notice.
    </P>
    <LinkRow
      links={[
        { label: "Make a privacy request", href: "mailto:info@standard.gm?subject=Privacy%20request" },
      ]}
    />

    <H2>Children and young readers</H2>
    <P>
      The Standard is a general news publication and is not directed at children under 13. We do
      not invite children to submit unnecessary personal information. A parent or guardian who
      believes a child has supplied personal information can contact us for review and appropriate
      action. This age statement does not limit additional protections required by applicable law.
    </P>

    <H2>Contact and updates</H2>
    <P>
      For privacy questions or requests, email info@standard.gm with the subject 'Privacy request'.
      Include the relevant page or service and enough detail to explain your request. We may ask
      for additional information only where needed.
    </P>
    <P>
      This notice may change as the website and its services develop. The revision date appears
      above. Material changes should be communicated as appropriate, and fresh consent obtained
      where required; a policy update alone does not replace required consent.
    </P>
    <LinkRow
      links={[
        { label: "Email The Standard", href: "mailto:info@standard.gm?subject=Privacy%20request" },
        { label: "Contact the newsroom", href: "/contact-us", testid: "privacy-policy-link-contact" },
      ]}
    />
  </LegalShell>
);

/* ------------- Your Privacy Choices (content from standard.gm/your-privacy-choices) ------------- */

export const PrivacyChoicesPage = () => (
  <LegalShell
    testid="privacy-choices"
    title="Your Privacy Choices"
    intro="Practical controls and contact routes for managing privacy when using The Standard."
  >
    <H2>Manage cookies and browser storage</H2>
    <p className="mt-4 text-[15px] leading-8 text-[var(--muted)]">Optional visitor analytics, third-party advertising, ad measurement and embedded videos are off until you allow them. Your preference is remembered for six months in this browser.</p>
    <button onClick={openCookiePreferences} className="mt-4 rounded-[6px] bg-[#cf0a0a] px-6 py-3 text-sm font-bold text-white hover:bg-[#141618]">Manage Cookie Preferences</button>
    <P>
      Use your browser's privacy settings to review, block or delete cookies and site data. You can
      also restrict third-party cookies. Blocking some storage may affect security checks, video
      playback or other features. Repeat your choices for each browser and device you use.
    </P>
    <P>
      Clearing browser storage can remove saved preferences. Private browsing limits what is
      retained on your device but does not make you anonymous to websites, your network or service
      providers.
    </P>
    <LinkRow
      links={[
        { label: "Chrome cookie settings", href: "https://support.google.com/chrome/answer/95647" },
        { label: "Firefox privacy settings", href: "https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" },
        { label: "Safari privacy guide", href: "https://support.apple.com/guide/safari/prevent-cross-site-tracking-sfri40732/mac" },
      ]}
    />

    <H2>Control personalised advertising</H2>
    <P>
      If you encounter Google advertising on The Standard or through a video player, Google's
      controls let you review and adjust ad personalisation. Turning personalisation off does not
      remove all advertisements and does not necessarily stop measurement, fraud prevention or
      other processing.
    </P>
    <P>
      These controls are operated by Google and apply according to its policies and your account or
      device settings. They do not automatically change another advertiser's practices or the
      campaign impression and click totals in The Standard's direct-ad system.
    </P>
    <LinkRow
      links={[
        { label: "Open Google My Ad Center", href: "https://myadcenter.google.com/" },
        { label: "How Google uses information", href: "https://policies.google.com/technologies/partner-sites" },
      ]}
    />

    <H2>Google Analytics options</H2>
    <P>
      Where a website uses Google Analytics, Google's browser opt-out add-on provides an additional
      control for supported browsers. This is a provider tool; it is not a site-wide switch for
      every analytics or logging service. It does not stop essential delivery and security logs.
    </P>
    <LinkRow
      links={[{ label: "Google Analytics opt-out add-on", href: "https://tools.google.com/dlpage/gaoptout" }]}
    />

    <H2>Videos and social sharing</H2>
    <P>
      You can choose not to play an embedded video or use a social sharing link. Loading images and
      thumbnails may still send technical information to the servers that host them. Playing a
      YouTube video communicates with YouTube even when its privacy-enhanced player is used.
    </P>
    <P>
      If you choose to share a story on Facebook, WhatsApp or X, that provider processes your
      interaction under its own privacy rules. You can instead use Copy link and choose how and
      where to share the URL.
    </P>
    <LinkRow
      links={[{ label: "Google and YouTube Privacy Policy", href: "https://policies.google.com/privacy" }]}
    />

    <H2>Choose what you send and receive</H2>
    <P>
      Only include information needed for an enquiry. Do not put sensitive details into the website
      search box. If you have separately subscribed to a newsletter or marketing service, use the
      unsubscribe link provided in its messages or contact the publisher about that service.
    </P>
    <P>
      A request to stop optional marketing does not necessarily stop a reply to an enquiry you
      sent, or a notice required for a service you use. Email settings and requests do not
      automatically change the controls held by an external platform.
    </P>

    <H2>Make a privacy request</H2>
    <P>
      Email info@standard.gm with the subject 'Privacy request'. Tell us whether you want to ask
      about access, correction, deletion, withdrawal of consent or another concern. Include the
      relevant service, correspondence or article URL, and how we can reply.
    </P>
    <P>
      Please avoid attaching identity documents unless we specifically ask and explain why they are
      needed. We may need proportionate information to identify the records and verify the request.
      Any request is assessed under applicable law, including lawful journalistic and
      public-interest considerations.
    </P>
    <P>
      The buttons below open an email draft; they do not submit a request automatically or change
      browser preferences.
    </P>
    <LinkRow
      links={[
        { label: "Request access to information", href: "mailto:info@standard.gm?subject=Privacy%20request%20-%20access" },
        { label: "Request a correction or deletion", href: "mailto:info@standard.gm?subject=Privacy%20request%20-%20correction%20or%20deletion" },
        { label: "Withdraw consent or raise a concern", href: "mailto:info@standard.gm?subject=Privacy%20request%20-%20consent%20or%20concern" },
      ]}
    />

    <H2>What these choices cover</H2>
    <P>
      Browser and provider settings affect the services they control. Visiting this page does not
      itself turn off all cookies or tracking, and it does not send a universal opt-out instruction
      to every external provider. If a consent preference tool is introduced, its controls and
      scope will be explained alongside it.
    </P>
    <P>
      For more about the information processed, recipients and your requests, read our Privacy
      Policy. If you remain concerned about the handling of a request, you may contact the
      competent authority under applicable law.
    </P>
    <LinkRow
      links={[
        { label: "Read the Privacy Policy", href: "/privacy-policy", testid: "privacy-choices-link-policy" },
        { label: "Contact the newsroom", href: "/contact-us", testid: "privacy-choices-link-contact" },
      ]}
    />
  </LegalShell>
);

/* ------------------- Contact Us (content from standard.gm/contact-us) ------------------- */

export const ContactUsPage = () => (
  <LegalShell
    testid="contact-us"
    title="Contact Us"
    intro="Have a story, question or advertising enquiry? Reach The Standard at our Fajara office or use the contact details below."
  >
    <div className="grid gap-5 md:grid-cols-2 mt-2">
      <div className="border border-[var(--line)] p-6 sm:p-8" data-testid="contact-call-card">
        <H2>Call us</H2>
        <P>Telephone numbers listed by The Standard:</P>
        <div className="mt-5 space-y-2 text-xl font-semibold font-display">
          <p>
            <a href="tel:+220877452126" data-testid="contact-phone-1" className="text-[#cf0a0a] hover:underline">
              +220 87 745 2126
            </a>
          </p>
          <p>
            <a href="tel:+220877081234" data-testid="contact-phone-2" className="text-[#cf0a0a] hover:underline">
              +220 87 708 1234
            </a>
          </p>
        </div>
      </div>
      <div className="border border-[var(--line)] p-6 sm:p-8" data-testid="contact-visit-card">
        <H2>Visit us</H2>
        <address className="mt-4 text-[15px] not-italic leading-8 text-[var(--muted)]">
          XL Standard Co. Ltd.
          <br />
          Kairaba Avenue, Fajara
          <br />
          Opposite The Butchers Shop Restaurant
          <br />
          The Gambia
        </address>
      </div>
    </div>

    <H2>News tips, feedback and advertising</H2>
    <P>
      For story ideas, feedback or advertising enquiries, call the office using either number above.
      For a correction, include the story link and the information you want us to review.
    </P>
    <LinkRow
      links={[
        { label: "Send a correction or feedback", href: "/corrections-feedback", testid: "contact-link-corrections" },
      ]}
    />
  </LegalShell>
);

/* ------------------- Corrections & Feedback (content from standard.gm/corrections-feedback) ------------------- */

export const CorrectionsPage = () => (
  <LegalShell
    testid="corrections-feedback"
    title="Corrections & Feedback"
    intro="If you spot something that may need correcting, or have feedback on our reporting, please tell us so we can review it."
  >
    <H2>Report a possible error</H2>
    <P>
      Please give us the story headline or link, identify the passage that concerns you, explain
      what you believe is inaccurate, and provide any supporting information you can share. Include
      a way to contact you if you would like us to follow up.
    </P>
    <P>
      Call our office at{" "}
      <a href="tel:+220877452126" className="font-semibold text-[#cf0a0a] hover:underline">
        +220 87 745 2126
      </a>{" "}
      or{" "}
      <a href="tel:+220877081234" className="font-semibold text-[#cf0a0a] hover:underline">
        +220 87 708 1234
      </a>
      . We will review the information you provide and decide what action is appropriate.
    </P>

    <H2>Share feedback</H2>
    <P>
      Your comments about our coverage, the website and topics you would like to see reported are
      welcome. Please be specific so your feedback reaches the right people.
    </P>
  </LegalShell>
);

/* ------------- About Us: verified publication and leadership details ------------- */

export const AboutUsPage = () => (
  <LegalShell
    testid="about-us"
    title="About Us"
    intro="A Gambian newspaper, in print and online since 2010. News, reporting and perspectives from The Gambia, Africa and the wider world."
  >
    <H2>Our story</H2>
    <P>
      Established in {publication.established}, The Standard is a privately owned Gambian newspaper
      and online news publication. From our offices in Fajara, we cover the people, events and
      decisions shaping life in The Gambia, alongside developments across Africa and the world.
    </P>
    <P>
      Our newspaper and website connect readers with daily news, interviews, features, sport and
      opinion. Readers can follow our coverage in print and at standard.gm, whether they are in
      The Gambia or following events from abroad.
    </P>

    <H2>Ownership and publisher</H2>
    <P>
      The Standard Newspaper is owned, published and printed by {publication.company}, a company
      based in The Gambia. Our office is on Kairaba Avenue, Fajara, opposite The Butchers Shop
      Restaurant.
    </P>

    <H2>Our leadership</H2>
    <dl className="mt-5 grid gap-4 sm:grid-cols-2" data-testid="about-leadership">
      {publication.leadership.map(person => (
        <div key={person.name} className="rounded-lg bg-[var(--bg-soft)] px-5 py-5">
          <dt className="font-display text-xl font-bold text-[var(--ink)]">{person.name}</dt>
          <dd className="mt-1 text-[14px] leading-6 text-[var(--muted)]">{person.role}</dd>
        </div>
      ))}
    </dl>
    <P>
      As Managing Editor, Sheriff Bojang leads the newspaper’s editorial work. Our reporters and
      contributors are credited on their stories, with author pages bringing their reporting
      together in one place.
    </P>

    <H2>Our reporters and contributors</H2>
    <P>
      Our stories identify their writers through individual bylines. Explore reporting and
      contributions by Arret Jatta, Omar Bah, Tabora Bojang and Lamin Cham through their author
      pages, with links to their published work.
    </P>
    <LinkRow links={[
      { label: "Arret Jatta", href: "/author/19/" },
      { label: "Omar Bah", href: "/author/16/" },
      { label: "Tabora Bojang", href: "/author/7/" },
      { label: "Lamin Cham", href: "/author/18/" },
    ]} />

    <H2>What we cover</H2>
    <P>
      Our coverage includes Gambian news and public affairs, politics, business, technology,
      health, education, sport, society and culture. Interviews and features explore the lives
      and experiences behind the headlines, while our opinion pages provide space for commentary
      and debate.
    </P>
    <LinkRow links={[
      { label: "Latest news", href: "/news" },
      { label: "Sports", href: "/category/sports/" },
      { label: "Opinions", href: "/category/opinions/" },
      { label: "Bantaba", href: "/category/bantaba/" },
    ]} />

    <H2>Corrections and reader feedback</H2>
    <P>
      Readers can contact the newsroom with news tips, questions about a story or concerns about
      accuracy. To request a correction, please provide the article link or headline, identify
      the information you believe is incorrect and include any supporting details. Our
      Corrections &amp; Feedback page explains how to get in touch.
    </P>
    <LinkRow links={[
      { label: "Corrections & Feedback", href: "/corrections-feedback", testid: "about-link-corrections" },
    ]} />

    <H2>Advertising and sponsored content</H2>
    <P>
      The Standard also carries advertising and sponsored content. Articles in our Sponsored
      Content section carry a disclosure above the headline identifying them as paid promotional
      content. Businesses and organisations can contact us about commercial placements through
      our advertising page. Advertising
      enquiries and news tips have separate purposes; please let us know which your enquiry
      concerns so it can reach the appropriate team.
    </P>
    <LinkRow links={[
      { label: "Advertise with us", href: "/advertise-with-us" },
      { label: "Sponsored content", href: "/category/advertisement/" },
    ]} />

    <H2>Contact The Standard</H2>
    <P>
      Visit us at {publication.address}, or use our Contact Us page for our telephone numbers and
      contact details. We welcome enquiries from readers, sources, contributors and advertisers.
    </P>
    <LinkRow links={[
      { label: "Contact Us", href: "/contact-us", testid: "about-link-contact" },
    ]} />
  </LegalShell>
);

/* ------------- Advertise with us (contact routes and surfaces from standard.gm) ------------- */

export const AdvertisePage = () => (
  <LegalShell
    testid="advertise-with-us"
    title="Advertise with us"
    intro="This space connects Gambian businesses with our readers across the country and the diaspora. Every placement is a paid partner placement."
  >
    <H2>Why advertise with The Standard</H2>
    <P>
      Our reporting covers politics, business, technology, sports and the stories shaping everyday
      life in The Gambia, alongside news from across Africa and the wider world — read at home and
      by the diaspora.
    </P>

    <H2>Where your brand can appear</H2>
    <UL
      items={[
        "Sponsored story placements across standard.gm.",
        "The homepage sponsored strip, shown to every reader on the front page.",
        "The Sponsored Content section, where partner stories sit alongside our reporting.",
        "Campaign reporting with impression and click totals, so you can see how each placement performs.",
      ]}
    />

    <H2>Book a placement</H2>
    <P>
      To advertise, write to ads@standard.gm with your campaign idea, or call the office using
      either number. Our team will come back to you with availability and a quote.
    </P>
    <LinkRow
      links={[
        { label: "Email ads@standard.gm", href: "mailto:ads@standard.gm", testid: "advertise-email" },
        { label: "Call +220 87 745 2126", href: "tel:+220877452126", testid: "advertise-phone-1" },
        { label: "Call +220 87 708 1234", href: "tel:+220877081234", testid: "advertise-phone-2" },
        { label: "Contact Us", href: "/contact-us", testid: "advertise-link-contact" },
      ]}
    />
  </LegalShell>
);

export default PrivacyPolicyPage;
